import { Badge } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import styles from './MpBookingDetail.module.css';

export interface BookingDetail {
  id: string;
  property_name: string;
  guest_name: string;
  guest_nationality?: string;
  guest_address?: string;
  guest_guests?: number;
  guest_adults?: number;
  guest_rating?: number;
  guest_reviews?: number;
  ota: string;
  ota_reservation_id?: string;
  check_in: string;
  check_out: string;
  nights: number;
  amount: number;
  per_night?: number;
  ota_fee?: number;
  cleaning_assignee?: string;
  cleaning_time?: string;
  days_used?: number;
  max_days?: number;
  smart_lock_code?: string;
  wifi_ssid?: string;
  wifi_password?: string;
  address?: string;
}

const otaColors: Record<string, string> = {
  Airbnb: '#FF5A5F',
  STAY: '#2563EB',
  Booking: '#003580',
};

const otaLabels: Record<string, string> = {
  Airbnb: 'Airbnb',
  STAY: 'STAY JAPAN',
  Booking: 'Booking.com',
};

interface Props {
  booking: BookingDetail;
  onMessage?: () => void;
  onCancel?: () => void;
}

export function MpBookingDetailContent({ booking, onMessage, onCancel }: Props) {
  const b = booking;
  const isToday = (() => {
    const today = new Date();
    const ci = new Date(b.check_in);
    return ci.toDateString() === today.toDateString();
  })();

  const formatDate = (d: string) => {
    const date = new Date(d);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };
  const formatDateTime = (d: string) => {
    const date = new Date(d);
    return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const perNight = b.per_night ?? (b.nights > 0 ? Math.round(b.amount / b.nights) : 0);
  const otaFee = b.ota_fee ?? 0;

  return (
    <div className={styles.content}>
      {/* Action buttons */}
      <div className={styles.actionBar}>
        <button className={styles.msgBtn} onClick={onMessage}>💬 メッセージ</button>
        <button className={styles.cancelBtn} onClick={onCancel}>❌ キャンセル</button>
      </div>

      {/* Guest header */}
      <div className={styles.guestHeader}>
        <div className={styles.otaBadge} style={{ background: otaColors[b.ota] ?? 'var(--brand)' }}>
          {otaLabels[b.ota] ?? b.ota}
        </div>
        <div className={styles.guestName}>{b.guest_name}</div>
        <div className={styles.guestMeta}>
          {otaLabels[b.ota] ?? b.ota}
          {b.ota_reservation_id && <> ｜ 予約ID: {b.ota_reservation_id}</>}
          {b.guest_rating && <> ｜ ⭐ {b.guest_rating}（{b.guest_reviews ?? 0}件のレビュー）</>}
        </div>
      </div>

      {/* Status badge */}
      {isToday && (
        <div className={styles.statusBanner}>
          <Badge variant="amber">チェックイン当日</Badge>
        </div>
      )}

      {/* Stay details */}
      <PanelSection>
        <div className={styles.stayGrid}>
          <div className={styles.stayItem}>
            <div className={styles.stayLabel}>チェックイン</div>
            <div className={styles.stayValue}>{formatDateTime(b.check_in)}</div>
          </div>
          <div className={styles.stayItem}>
            <div className={styles.stayLabel}>チェックアウト</div>
            <div className={styles.stayValue}>{formatDateTime(b.check_out)}</div>
          </div>
          <div className={styles.stayItem}>
            <div className={styles.stayLabel}>宿泊数</div>
            <div className={styles.stayValue}>{b.nights}泊</div>
          </div>
          <div className={styles.stayItem}>
            <div className={styles.stayLabel}>人数</div>
            <div className={styles.stayValue}>{b.guest_guests ?? 2}名</div>
          </div>
          <div className={styles.stayItem}>
            <div className={styles.stayLabel}>物件</div>
            <div className={styles.stayValue}>{b.property_name}</div>
          </div>
        </div>
      </PanelSection>

      {/* Pricing */}
      <PanelSection title="精算額">
        <div className={styles.priceTotal}>¥{b.amount.toLocaleString()}</div>
        <div className={styles.priceBreakdown}>
          <div className={styles.priceRow}>
            <span>宿泊単価</span>
            <span className={styles.priceVal}>¥{perNight.toLocaleString()}/泊</span>
          </div>
          <div className={styles.priceRow}>
            <span>{otaLabels[b.ota] ?? b.ota}手数料</span>
            <span className={styles.priceVal}>−¥{otaFee.toLocaleString()}</span>
          </div>
        </div>
        {b.days_used !== undefined && b.max_days !== undefined && (
          <div className={styles.daysLimit}>
            今年度営業日数 <strong>{b.days_used}日</strong> / {b.max_days}日上限
          </div>
        )}
      </PanelSection>

      {/* Check-in info */}
      <PanelSection title="チェックイン情報（ゲストへ送付済み）">
        <div className={styles.checkinInfo}>
          <div className={styles.checkinItem}>📍 {b.property_name} チェックイン案内</div>
          {b.address && <div className={styles.checkinItem}>🏠 住所: {b.address}</div>}
          {b.smart_lock_code && (
            <div className={styles.checkinItem}>🔑 スマートロック番号: {b.smart_lock_code}（チェックイン当日のみ有効）</div>
          )}
          {b.wifi_ssid && (
            <div className={styles.checkinItem}>📶 Wi-Fi: {b.wifi_ssid}{b.wifi_password ? ` ｜ パスワード: ${b.wifi_password}` : ''}</div>
          )}
          <div className={styles.checkinItem}>🧹 チェックアウト時: 使用済みタオルをバスルームへ、ゴミを所定の袋へ</div>
        </div>
      </PanelSection>

      {/* Cleaning task */}
      <PanelSection title="清掃タスク">
        <div className={styles.cleaningTask}>
          <div className={styles.cleaningHeader}>チェックアウト後清掃</div>
          <div className={styles.cleaningMeta}>
            {formatDateTime(b.check_out)}以降 ｜ 担当: {b.cleaning_assignee ?? '未割当'}
          </div>
          {!b.cleaning_assignee && (
            <button className={styles.assignBtn}>👤 割当</button>
          )}
        </div>
      </PanelSection>

      {/* Guest registry (住宅宿泊事業法) */}
      <PanelSection title="宿泊者名簿（住宅宿泊事業法 義務）">
        <SpecGrid>
          <SpecCell label="氏名" value={b.guest_name} />
          <SpecCell label="国籍" value={b.guest_nationality ?? '—'} />
          <SpecCell label="住所（国）" value={b.guest_address ?? '—'} />
          <SpecCell label="人数" value={`${b.guest_guests ?? 2}名（成人${b.guest_adults ?? b.guest_guests ?? 2}）`} />
        </SpecGrid>
        <div className={styles.legalNote}>
          ※ 宿泊者名簿は3年間保存義務。住縁 Mirai EMS が自動保存します。
        </div>
      </PanelSection>
    </div>
  );
}
